import {
  DEFAULT_READER,
  READER_KEY,
  readPreferences,
} from '@/lib/preferences/readerPreferencesModel';
import { LISTEN_SETTINGS_KEY, parseListenSettings } from '@/lib/listen/settings';
import type { ListenSettings } from '@/lib/listen/settings';
import type { PlateGroup } from '@/lib/plates/types';

export interface Settings {
  appearance: typeof DEFAULT_READER.appearance;
  accent: typeof DEFAULT_READER.accent;
  rail_side: 'left' | 'right';
  menu: 'shown' | 'hidden';
  type_scale: number;
  content_scale: number;
  elements: 'plain' | 'plates';
  elements_words: PlateGroup[];
  agent_enabled: boolean;
  team_enabled: boolean;
  team_standing_notes: string;
  notify_mutes: Record<string, string | null>;
  notify_events: Record<string, { push: boolean; email: boolean; in_app: boolean }>;
  notify_quiet_hours: { start: string; end: string } | null;
  notify_time_zone: string;
  notify_detail: 'titles' | 'generic';
  notify_email_fallback: boolean;
  listen: ListenSettings;
  shelf_rules_hint_seen?: number;
  shelf_rules_hint_retired?: boolean;
}
export type Pending = Partial<
  Record<keyof Settings, { value: unknown; base: unknown; changedAt: number }>
>;
const groups: PlateGroup[] = ['Text', 'Media', 'Data', 'Diagram', 'Interactive'];
export const SETTINGS_EVENT = 'knowsh:settings:changed';
let subject: string | undefined;
const listeners = new Set<() => void>();
export const subscribeSettings = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};
export function stored(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}
export function save(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* Keep the in-memory choice. */
  }
}
function json(raw: string | null): unknown {
  try {
    return JSON.parse(raw ?? 'null');
  } catch {
    return null;
  }
}
export const accountKey = (key: string, sub = subject) =>
  sub ? `${key}:${encodeURIComponent(sub)}` : key;
function legacy(key: string, sub?: string) {
  const scoped = key === READER_KEY ? accountKey(key, sub) : sub ? `${key}:${sub}` : key;
  return stored(scoped) ?? stored(key);
}
export function readCachedSettings(sub?: string): Settings {
  const r = readPreferences(legacy(READER_KEY, sub));
  const words = json(legacy('knowsh.elements.words', sub));
  return {
    appearance: r.appearance,
    accent: r.accent,
    rail_side: r.railSide,
    menu: r.menu,
    type_scale: r.typeScale,
    content_scale: r.contentScale,
    elements: legacy('knowsh.elements', sub) === 'plates' ? 'plates' : 'plain',
    elements_words: Array.isArray(words) ? groups.filter((g) => words.includes(g)) : [],
    agent_enabled: legacy('knowsh:agent.enabled', sub) !== '0',
    team_enabled: sub ? stored(accountKey('knowsh.team.enabled', sub)) === '1' : false,
    team_standing_notes: sub ? (stored(accountKey('knowsh.team.notes', sub)) ?? '') : '',
    ...notificationSettings(sub ? json(stored(accountKey('knowsh.notify.settings', sub))) : null),
    notify_mutes: sub ? parseMutes(json(stored(accountKey('knowsh.notify.mutes', sub)))) : {},
    listen: parseListenSettings(json(legacy(LISTEN_SETTINGS_KEY, sub))),
    shelf_rules_hint_seen: Number(legacy('knowsh.shelf_rules_hint_seen', sub) ?? '0'),
    shelf_rules_hint_retired: legacy('knowsh.shelf_rules_hint_retired', sub) === '1',
  };
}
export const defaultSettings = (): Settings => ({
  appearance: 'system',
  accent: 'softBlue',
  rail_side: 'right',
  menu: 'shown',
  type_scale: 1,
  content_scale: 1,
  elements: 'plain',
  elements_words: [],
  agent_enabled: true,
  team_enabled: false,
  team_standing_notes: '',
  notify_mutes: {},
  ...notificationSettings(null),
  listen: parseListenSettings(null),
  shelf_rules_hint_seen: 0,
  shelf_rules_hint_retired: false,
});
export function serverSettings(value: Partial<Settings>): Settings {
  const r = readPreferences(
    JSON.stringify({
      appearance: value.appearance,
      accent: value.accent,
      railSide: value.rail_side,
      menu: value.menu,
      typeScale: value.type_scale,
      contentScale: value.content_scale,
    }),
  );
  return {
    appearance: r.appearance,
    accent: r.accent,
    rail_side: r.railSide,
    menu: r.menu,
    type_scale: r.typeScale,
    content_scale: r.contentScale,
    elements: value.elements === 'plates' ? 'plates' : 'plain',
    elements_words: groups.filter((g) => value.elements_words?.includes(g)),
    agent_enabled: value.agent_enabled !== false,
    team_enabled: value.team_enabled === true,
    ...notificationSettings(value),
    team_standing_notes:
      typeof value.team_standing_notes === 'string' ? value.team_standing_notes.slice(0, 1000) : '',
    notify_mutes: parseMutes(value.notify_mutes),
    listen: parseListenSettings(value.listen),
    shelf_rules_hint_seen:
      typeof value.shelf_rules_hint_seen === 'number' ? value.shelf_rules_hint_seen : 0,
    shelf_rules_hint_retired: value.shelf_rules_hint_retired === true,
  };
}
export function notificationSettings(
  raw: unknown,
): Pick<
  Settings,
  | 'notify_events'
  | 'notify_quiet_hours'
  | 'notify_time_zone'
  | 'notify_detail'
  | 'notify_email_fallback'
> {
  const value = (raw && typeof raw === 'object' ? raw : {}) as Partial<Settings>;
  const quiet = value.notify_quiet_hours;
  return {
    notify_events: value.notify_events ?? {},
    notify_quiet_hours:
      quiet &&
      /^([01]\d|2[0-3]):[0-5]\d$/.test(quiet.start) &&
      /^([01]\d|2[0-3]):[0-5]\d$/.test(quiet.end)
        ? quiet
        : null,
    notify_time_zone: value.notify_time_zone || Intl.DateTimeFormat().resolvedOptions().timeZone,
    notify_detail: value.notify_detail === 'titles' ? 'titles' : 'generic',
    notify_email_fallback: value.notify_email_fallback === true,
  };
}
function parseMutes(value: unknown): Record<string, string | null> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  return Object.fromEntries(
    Object.entries(value).filter(
      ([, until]) =>
        until === null || (typeof until === 'string' && Number.isFinite(Date.parse(until))),
    ),
  );
}
export const settingsCache = {
  value: readCachedSettings(),
  pending: {} as Pending,
  loaded: false,
  subject,
};
function publish() {
  listeners.forEach((fn) => fn());
  window.dispatchEvent(new Event(SETTINGS_EVENT));
}
export function persistSettings() {
  const v = settingsCache.value;
  const sub = settingsCache.subject;
  const key = (name: string) => (sub ? `${name}:${sub}` : name);
  save(
    accountKey(READER_KEY, sub),
    JSON.stringify({
      appearance: v.appearance,
      accent: v.accent,
      railSide: v.rail_side,
      menu: v.menu,
      typeScale: v.type_scale,
      contentScale: v.content_scale,
    }),
  );
  save(key('knowsh.elements'), v.elements);
  save(key('knowsh.elements.words'), JSON.stringify(v.elements_words));
  save(key('knowsh:agent.enabled'), v.agent_enabled ? '1' : '0');
  if (sub) {
    save(accountKey('knowsh.team.enabled', sub), v.team_enabled ? '1' : '0');
    save(accountKey('knowsh.team.notes', sub), v.team_standing_notes);
    save(accountKey('knowsh.notify.mutes', sub), JSON.stringify(v.notify_mutes));
    save(accountKey('knowsh.notify.settings', sub), JSON.stringify(notificationSettings(v)));
  }
  save(key(LISTEN_SETTINGS_KEY), JSON.stringify(v.listen));
  save(key('knowsh.shelf_rules_hint_seen'), String(v.shelf_rules_hint_seen ?? 0));
  save(key('knowsh.shelf_rules_hint_retired'), v.shelf_rules_hint_retired ? '1' : '0');
  if (sub) save(accountKey('knowsh.settings.pending', sub), JSON.stringify(settingsCache.pending));
  publish();
}
export function setSettingsIdentity(sub?: string) {
  subject = sub;
  settingsCache.subject = sub;
  settingsCache.loaded = false;
  settingsCache.value = readCachedSettings(sub);
  const pending = sub ? json(stored(accountKey('knowsh.settings.pending', sub))) : null;
  settingsCache.pending = pending && typeof pending === 'object' ? (pending as Pending) : {};
  publish();
}
export function updateSettings(patch: Partial<Settings>) {
  const previous = settingsCache.value;
  settingsCache.value = { ...previous, ...patch };
  if (settingsCache.subject)
    for (const key of Object.keys(patch) as (keyof Settings)[]) {
      settingsCache.pending[key] = {
        value: patch[key],
        base: settingsCache.pending[key]?.base ?? previous[key],
        changedAt: Date.now(),
      };
    }
  persistSettings();
}
